import { useCallback, useEffect, useRef, useState } from 'react';

export type PageResult<Item, Extra> =
  | { items: Item[]; hasMore: boolean; next: string | null; extra?: Extra }
  | { error: string; status?: number };

// Everything loaded so far, tagged with the query it answers.
type Loaded<Item, Extra> = {
  key: string;
  items: Item[];
  extra: Extra | null;
  hasMore: boolean;
  next: string | null;
  error: string | null;
  errorStatus: number | null;
};

// A cursor-paged list: the first page loads when `key` is set, later pages
// append on loadMore, and a new key starts over. `key` names the query (a
// campus and category, a series). Null sits idle.
//
// What's loaded is kept together with the key it was loaded for, and only
// shown while that's still the key asked for. So the render after the key
// changes -- before any effect has run -- already reads as loading, rather
// than showing the last query's results, or an empty state, for a frame.
//
// `extra` carries anything a page brings besides its items, such as the
// series a page of its services arrives with; the latest one is kept.
export default function usePagedList<Item, Extra = undefined>(
  key: string | null,
  fetchPage: (cursor: string | null) => Promise<PageResult<Item, Extra>>
) {
  const [loaded, setLoaded] = useState<Loaded<Item, Extra> | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  // A refetch of the query already shown, which keeps showing it meanwhile.
  const [isRefetching, setIsRefetching] = useState<boolean>(false);

  // Read through a ref so callers can pass an inline function without it
  // counting as a new query every render; `key` is what says that.
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const latestRequest = useRef(0);

  const load = useCallback(async (cursor: string | null) => {
    if (key === null) return;

    const request = ++latestRequest.current;
    const isFirstPage = cursor === null;

    if (isFirstPage) {
      setIsRefetching(true);
    } else {
      setIsLoadingMore(true);
    }

    const result = await fetchPageRef.current(cursor);

    // A newer request, or a different query, has taken over.
    if (request !== latestRequest.current) return;

    setLoaded((current) => {
      const previous = current?.key === key ? current : null;

      if ('error' in result) {
        // A failed Load More keeps the pages already shown.
        return isFirstPage || !previous ?
          { key, items: [], extra: null, hasMore: false, next: null, error: result.error, errorStatus: result.status ?? null } :
          { ...previous, error: result.error, errorStatus: result.status ?? null };
      }

      return {
        key,
        // The first page replaces, so a refetch doesn't duplicate rows.
        items: isFirstPage || !previous ? result.items : [...previous.items, ...result.items],
        extra: result.extra !== undefined ? result.extra : previous?.extra ?? null,
        hasMore: result.hasMore && Boolean(result.next),
        next: result.next,
        error: null,
        errorStatus: null
      };
    });

    if (isFirstPage) {
      setIsRefetching(false);
    } else {
      setIsLoadingMore(false);
    }
  }, [key]);

  useEffect(() => {
    // Anything still in flight belongs to the old query.
    latestRequest.current++;
    setIsLoadingMore(false);
    setIsRefetching(false);

    if (key !== null) {
      void load(null);
    }
  }, [key, load]);

  const current = key !== null && loaded?.key === key ? loaded : null;
  const hasMore = current?.hasMore ?? false;
  const next = current?.next ?? null;

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || !next) return;

    void load(next);
  }, [hasMore, isLoadingMore, next, load]);

  const refetch = useCallback(() => load(null), [load]);

  return {
    items: current?.items ?? [],
    extra: current?.extra ?? null,
    // True from the first render with a new key until its first page is in,
    // and again during a refetch.
    loading: (key !== null && current === null) || isRefetching,
    hasLoaded: current !== null,
    error: current?.error ?? null,
    errorStatus: current?.errorStatus ?? null,
    hasMore,
    isLoadingMore,
    loadMore,
    refetch
  } as const;
}
